import React from "react"
import { useCallback, useEffect, useMemo, useRef, type Ref } from "react"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import type { OpLayoutEngineRendererActions } from "connected-components/op-layout-engine/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { useModules } from "../context/modules"
import { useDashboardOpLayoutEnginePersistence } from "../hooks/use-op-layout-engine-persistence"
import { useDashboardModuleActions } from "../hooks/use-dashboard-module-actions"
import { createModuleComponents } from "../helpers/module-renderers"
import { IDashboardContextValue } from "../context/types"
import { type DashboardModule, MODULE_TYPES } from "../modules/types"

const DEFAULT_MODULE_TYPES = [MODULE_TYPES.ASSET_VIEW, MODULE_TYPES.KPI, MODULE_TYPES.GRAPH, MODULE_TYPES.FACTORS]

function isDefaultModulesOnly(modules: DashboardModule[]): boolean {
  if (modules.length !== DEFAULT_MODULE_TYPES.length) return false
  const types = new Set(modules.map((m) => m.type))
  if (DEFAULT_MODULE_TYPES.some((t) => !types.has(t))) return false
  return modules.every((m) => !m.config?.isSecondary)
}

interface DashboardModulesRendererProps {
  TradeContext: React.Context<IDashboardContextValue>
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  dockviewLayoutRef?: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
}

/**
 * Renders dashboard modules using OpLayoutEngine
 */
export default function DashboardModulesRenderer({
  TradeContext,
  instrumentsWithMetadata,
  hasMultipleInstruments,
  contentSets,
  modelMetadata,
  dockviewLayoutRef,
  onLayoutReady,
}: DashboardModulesRendererProps) {
  const { modules, isPreferencesLoaded: isModulesPreferencesLoaded, removeModule, updateModule, addModule, resetModulesToDefault } = useModules()
  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence(modules, isModulesPreferencesLoaded)
  const { handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule } = useDashboardModuleActions(modules, {
    removeModule,
    updateModule,
    addModule,
  })

  const pendingResetApiRef = useRef<Parameters<OpLayoutEngineRendererActions["resetLayout"]>[0] | null>(null)
  const didRequestResetRef = useRef(false)

  useEffect(() => {
    if (!didRequestResetRef.current || !pendingResetApiRef.current || !isDefaultModulesOnly(modules)) return
    const api = pendingResetApiRef.current
    const layout = dockviewLayoutRef as React.RefObject<OpLayoutEngineRendererActions | null>
    if (layout?.current) {
      layout.current.resetLayout(api)
    }
    pendingResetApiRef.current = null
    didRequestResetRef.current = false
  }, [modules, dockviewLayoutRef])

  const wrappedOnLayoutReady = useCallback(
    (actions: OpLayoutEngineRendererActions | null) => {
      if (!onLayoutReady) return
      if (!actions) {
        onLayoutReady(null)
        return
      }
      onLayoutReady({
        ...actions,
        resetLayout: (api) => {
          didRequestResetRef.current = true
          pendingResetApiRef.current = api
          resetModulesToDefault()
        },
      })
    },
    [onLayoutReady, resetModulesToDefault],
  )

  const components = useMemo(() => createModuleComponents(modules, {
    TradeContext,
    instrumentsWithMetadata,
    hasMultipleInstruments,
    contentSets,
    modelMetadata,
    onRemoveModule: handleRemoveModule,
    onUpdateModuleTitle: handleUpdateModuleTitle,
    onDuplicateModule: handleDuplicateModule,
    updateModule,
  }), [modules, TradeContext, contentSets, hasMultipleInstruments, instrumentsWithMetadata, modelMetadata, handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule, updateModule])

  return (
    <OpLayoutEngineRenderer
      layoutRef={dockviewLayoutRef ?? null}
      onLayoutReady={wrappedOnLayoutReady}
      components={components}
      template={TEMPLATES.HIERARCHICAL}
      persistence={opLayoutEnginePersistence}
      className="dashboard-op-layout"
    />
  )
}
